import {RendererProps} from "../RendererProps";
import {ReactElement, useMemo} from "react";
import {budgetSeasonString, ensureNotObject, renderPossibleArray} from "../common";
import {MinimalProperty} from "../../../../../contexts/properties/logic";
import {renewalIncreaseTypePrettyName} from "../../../../workflows/operational/renewal-rates/renewal-increase-helpers";
import {OriginalRevenueRenewalIncreaseType} from "../../../../../__generated__/generated_types";
import * as common from "../styles/common.module.scss";


function renderBudgetReforecastObj(obj: any): ReactElement | null {
    if(typeof obj !== "object" || obj == null) {
        return null;
    }

    const moveOutPercentage = obj["moveOutPercentage"];
    const renewalRatio = obj["renewalRatio"];
    const occupancyPercentage = obj["occupancyPercentage"];
    const renewalTradeOut = obj["renewalTradeOut"];
    const marketRentGrowth = obj["marketRentGrowth"];

    const moveOutPercentageRender = renderPossibleArray(moveOutPercentage);
    const renewalRatioRender = renderPossibleArray(renewalRatio);
    const occupancyPercentageRender = renderPossibleArray(occupancyPercentage);
    const renewalTradeOutRender = renderPossibleArray(renewalTradeOut);
    const marketRentGrowthRender = renderPossibleArray(marketRentGrowth);

    return <>
        {moveOutPercentageRender !== "" && <div className={common.section}>Move Out Percentage: {moveOutPercentageRender}</div>}
        {renewalRatioRender !== "" && <div className={common.section}>Renewal Percentage: {renewalRatioRender}</div>}
        {occupancyPercentageRender !== "" && <div className={common.section}>Occupancy: {occupancyPercentageRender}</div>}
        {renewalTradeOutRender !== "" && <div className={common.section}>Renewal Tradeout: {renewalTradeOutRender}</div>}
        {marketRentGrowthRender !== "" && <div className={common.section}>Market Rent Growth: {marketRentGrowthRender}</div>}
    </>;
}

export interface RenderSubmitBulkSetAssumptionsProps extends RendererProps {
    properties: MinimalProperty[];
}

export function RenderSubmitBulkSetAssumptions(props: RenderSubmitBulkSetAssumptionsProps): ReactElement {
    const propertyMap = useMemo(() => {
        return props.properties.toIdMap(p => p.id);
    }, [props.properties]);

    const budgetVals = props.variables["budget"];
    const reforecastVals = props.variables["reforecast"];
    const propertyIds = props.variables["propertyIds"];
    const budgetYear = props.variables["budgetYear"];

    // Expirations
    const expirationCurve = props.variables["expirationCurve"];
    const setReforecastExpirationsFromPms = props.variables["setReforecastExpirationsFromPms"];

    const expirationCurveRender = renderPossibleArray(expirationCurve);

    // Renewal Tradeout
    const renewalTradeoutGrowIPR = props.variables["renewalTradeOutGrowInPlace"];
    const renewalTradeoutDiscountMarket = props.variables["renewalTradeOutDiscountMarket"];

    let propertyNames: string[] = [];
    if(Array.isArray(propertyIds) === false || propertyIds.length === 0) {
        propertyNames = propertyIds.map((p: unknown) => typeof p === "string" ? propertyMap[p]?.name ?? "Unknown" : "Unknown");
    }

    return <div>
        <div className={common.textHeader}>{budgetSeasonString(budgetYear)}</div>
        <div className={common.section}>Properties: {propertyNames.join(", ")}</div>
        {renewalTradeoutGrowIPR !== null && renewalTradeoutGrowIPR !== undefined &&
            <div className={common.section}>{renewalIncreaseTypePrettyName(OriginalRevenueRenewalIncreaseType.MarketOrInPlaceRent)} of {ensureNotObject(renewalTradeoutGrowIPR)}%</div>
        }
        {renewalTradeoutDiscountMarket !== null && renewalTradeoutDiscountMarket !== undefined &&
            <div className={common.section}>{renewalIncreaseTypePrettyName(OriginalRevenueRenewalIncreaseType.MarketRentDiscount)} of {ensureNotObject(renewalTradeoutDiscountMarket)}%</div>
        }
        <div>
            <div className={common.textSubHeader}>Reforecast</div>
            {setReforecastExpirationsFromPms && <div className={common.section}>Expirations Set from PMS</div>}
            {renderBudgetReforecastObj(reforecastVals)}
        </div>
        <div>
            <div className={common.textSubHeader}>Budget</div>
            {expirationCurveRender !== "" && <div className={common.section}>Expiration Curve: {expirationCurveRender}</div>}
            {renderBudgetReforecastObj(budgetVals)}
        </div>
    </div>;
}